import React from 'react';
import { PropTypes } from 'prop-types';
import Downshift from 'downshift';
import { MultiSearchSelectContainer } from './MultiSearchSelect.styles';
import MultiSearchSelectInput from './partials/MultiSearchSelectInput';
import MultiSearchSelectMenu from './partials/MultiSearchSelectMenu';
import { getFilteredOptions } from './MultiSearchSelect.utils';

function MultiSearchSelect(props) {
  const {
    name,
    label,
    options,
    twoLayerOptions,
    enableActions,
    selectAll,
    removeAll,
    remove,
    value,
    variant,
    handleChange,
    loading,
    readOnly,
    emptyMessage,
    styledProps,
    minCharacters,
    displayCount,
    required,
    onBlur,
    error,
    errorText,
    clearable,
    allSelectable,
    selectAllGuests,
    onInputChange,
    showSearchCount,
    singleSelectOnly,
    disabledInputChange
  } = props;

  return (
    <Downshift id={`multi-search-select-${name}`} onInputValueChange={onInputChange}>
      {(downshift) => {
        const { getRootProps, getInputProps, getMenuProps, openMenu, isOpen, inputValue, setState } = downshift;
        const inputProps = getInputProps({
          onClick: !readOnly && openMenu,
          value: inputValue || ''
        });
        const menuProps = getMenuProps();
        const { selectedOptions, filteredOptions, filteredTwoLayerOptions } = getFilteredOptions({
          options,
          inputValue,
          minCharacters,
          value,
          twoLayerOptions
        });
        const shrinkLabel = isOpen || !!value.length;
        const resetInput = () => setState({ inputValue: '' });

        return (
          <MultiSearchSelectContainer data-testid={name} {...getRootProps({ refKey: name })}>
            <MultiSearchSelectInput
              name={name}
              label={label}
              variant={variant}
              inputProps={inputProps}
              isOpen={isOpen}
              loading={loading}
              readOnly={readOnly}
              shrinkLabel={shrinkLabel}
              styledProps={styledProps}
              remove={remove}
              selectedOptions={selectedOptions}
              displayCount={displayCount}
              required={required}
              onBlur={onBlur}
              error={error}
              errorText={errorText}
              disabledInputChange={disabledInputChange}
            />
            {!readOnly && (
              <MultiSearchSelectMenu
                name={name}
                filteredOptions={filteredOptions}
                filteredTwoLayerOptions={filteredTwoLayerOptions}
                menuProps={menuProps}
                minCharacters={minCharacters}
                isOpen={isOpen}
                handleChange={handleChange}
                emptyMessage={emptyMessage}
                enableActions={enableActions}
                clearable={clearable}
                inputProps={inputProps}
                selectAll={() => {
                  selectAll(inputValue);
                  resetInput();
                }}
                removeAll={() => {
                  removeAll(inputValue);
                  resetInput();
                }}
                value={value}
                allSelectable={allSelectable}
                selectAllGuests={selectAllGuests}
                singleSelectOnly={singleSelectOnly}
                showSearchCount={showSearchCount}
              />
            )}
          </MultiSearchSelectContainer>
        );
      }}
    </Downshift>
  );
}

MultiSearchSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number])),
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['standard', 'outlined']),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  handleChange: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string,
  readOnly: PropTypes.bool,
  enableActions: PropTypes.bool,
  selectAll: PropTypes.func,
  removeAll: PropTypes.func,
  remove: PropTypes.func,
  styledProps: PropTypes.shape({}),
  minCharacters: PropTypes.number,
  displayCount: PropTypes.number,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  clearable: PropTypes.bool,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  allSelectable: PropTypes.bool,
  selectAllGuests: PropTypes.func,
  twoLayerOptions: PropTypes.shape({}),
  onInputChange: PropTypes.func,
  showSearchCount: PropTypes.bool,
  singleSelectOnly: PropTypes.bool,
  disabledInputChange: PropTypes.bool
};
MultiSearchSelect.defaultProps = {
  minCharacters: 0,
  name: '',
  label: '',
  value: [],
  loading: false,
  variant: 'standard',
  options: [],
  readOnly: false,
  enableActions: false,
  selectAll: () => {},
  removeAll: () => {},
  remove: () => {},
  emptyMessage: 'No result(s) found.',
  onBlur: () => {},
  required: false,
  error: false,
  clearable: false,
  errorText: '',
  styledProps: {},
  displayCount: 0,
  allSelectable: false,
  selectAllGuests: () => {},
  twoLayerOptions: {},
  onInputChange: () => {},
  showSearchCount: true,
  singleSelectOnly: false,
  disabledInputChange: false
};

export default MultiSearchSelect;
